import { endpoints } from '../data';
import type { EndpointOverlay } from './overlay';

// Traditional Chinese (Hong Kong) endpoint pages.

// Parameter descriptions shared across many endpoints. Endpoint-specific `params`
// entries override these.
const commonParams: Record<string, string> = {
  limit: '每頁結果數。',
  sort: '排序欄位。',
  order: '結果排序方向：asc(升冪) 或 desc(降冪)。',
  adjusted: '結果是否依除權除息調整，預設為 true。',
  ts_code: 'TS 證券代碼，部分資料集支援以逗號分隔多個代碼。',
  trade_date: '交易日期，格式為 YYYYMMDD。',
  start_date: '起始日期(含)，格式為 YYYYMMDD。',
  end_date: '結束日期(含)，格式為 YYYYMMDD。',
  fields: '以逗號分隔的輸出欄位，省略則回傳預設欄位。',
};

const aggregateParamsZh: Record<string, string> = {
  stocksTicker: '區分大小寫的市場代碼。',
  optionsTicker: '標準期權合約代碼。',
  multiplier: '時間跨度的倍數。',
  timespan: '聚合窗口：minute、hour、day、week、month、quarter 或 year。',
  from: '窗口起點，格式為 YYYY-MM-DD 或 Unix 毫秒時間戳。',
  to: '窗口終點，格式為 YYYY-MM-DD 或 Unix 毫秒時間戳。',
  adjusted: '結果是否依股票分割調整，預設為 true。',
  sort: '依時間戳排序：asc 或 desc。',
  limit: '查詢的基礎聚合上限，預設 5,000，最大 50,000。',
};


// --- 港股／A 股／全球目錄 ------------------------------------------------
// 英文目錄在 data.ts 由表格產生，這裡沿用同一種寫法：一份標題與摘要表格展開成
// 完整的翻譯覆蓋層，兩邊的端點清單才不會各自漂移。

const lbUseCasesZh = ['市場研究', '選股篩選', '資料管道', '應用整合'];

const lbParamsZh: Record<string, string> = {
  ticker: '標準格式代碼，例如 700.HK、000001.SZ、600519.SH 或 AAPL.US。HK:700、補零的港股代碼、.SS 與 .CN 都會自動正規化。',
  tickers: '以逗號分隔的標準格式代碼，最多 50 個。',
  language: '名稱、描述與敘述式摘要的回應語言：en(預設)、zh-CN 或 zh-HK。',
  period: '週期。',
  from: '起始日期，格式為 YYYY-MM-DD。',
  to: '結束日期，格式為 YYYY-MM-DD。',
  limit: '回傳筆數上限。',
  sort: '排序方向。',
  market: '市場代碼：US、HK、CN、SH、SZ、SG、JP 或 EU，預設 HK。',
  markets: '以逗號分隔的市場代碼，預設 HK。',
  date: '指定交易日，格式為 YYYY-MM-DD，預設為最近一個交易日。',
  keyword: '以指標名稱作全文篩選。',
  indicatorId: '指標 ID，來自「宏觀經濟指標」接口。',
  shareholderId: '股東物件 ID，來自「十大股東」接口。',
  comparisonTickers: '以逗號分隔的同業比較代碼。',
  currency: '報表貨幣，預設為該證券的上市貨幣。',
  key: '排行榜鍵值，來自「排行榜分類」接口，ib_ 前綴可省略。',
  brokerId: '經紀參與者編號，來自「經紀持倉」接口。',
  kind: '報表種類：IS(損益表)、BS(資產負債表)、CF(現金流量表)或 ALL，預設 ALL。',
  indexes: '以逗號分隔的計算指標名稱。',
  adjust: 'none(預設)或 forward(前復權)。',
  sessions: 'regular(預設)只取連續競價時段，all 則包含盤前、盤後與夜盤。',
  category: '上游異動類別代碼，預設 0 代表全部類別。',
  includeArticles: '是否一併回傳每檔證券的關聯文章，預設 false。',
};

const lbReadOnlyNoteZh = '本目錄的所有資料接口皆為唯讀，不提供帳戶、交易與自選股操作。';

// 市場覆蓋說明由英文目錄推導，翻譯後再套用，兩邊的覆蓋結論與示例網址才會一致。
const lbCoverageNotesZh: Record<string, string> = {
  'Markets: HK and China A-shares. U.S. quotes stay on the U.S. stock routes.':
    '支援市場：港股與中國 A 股。美股報價仍由既有的美股接口提供。',
  'Markets: HK and China A-shares. U.S. index constituents are not published upstream.':
    '支援市場：港股與中國 A 股。上游並未提供美股指數的成分股。',
  'Markets: HK only. Broker holdings come from HKEX CCASS.':
    '支援市場：僅港股。經紀持倉資料來自港交所 CCASS。',
  'Markets: HK only, for issuers also listed in mainland China.':
    '支援市場：僅港股，且限於同時在中國內地上市的發行人。',
  'Markets: HK, CN, and US. The feed is empty outside an active anomaly.':
    '支援市場：港股、A 股與美股。在沒有進行中的異動時，此接口會回傳空集合。',
  'Markets: HK and China A-shares. U.S. issuers return an empty list.':
    '支援市場：港股與中國 A 股。美股發行人會回傳空清單。',
  'Markets: HK and China A-shares, and only where the issuer publishes a hierarchy.':
    '支援市場：港股與中國 A 股，且僅限有披露層級關係的發行人。',
  'Markets: HK and China A-shares. No U.S. listings are published on this calendar.':
    '支援市場：港股與中國 A 股。此日曆並未收錄美股上市項目。',
  'Markets: HK and China A-shares. No U.S. meetings are published on this calendar.':
    '支援市場：港股與中國 A 股。此日曆並未收錄美股會議項目。',
  'Markets: HK only.': '支援市場：僅港股。',
};

function coverageNoteZh(note: string | undefined): string | undefined {
  if (!note) return undefined;
  if (lbCoverageNotesZh[note]) return lbCoverageNotesZh[note];
  const usPrefix = 'Markets: HK, China A-shares, and US. U.S. example: ';
  return note.startsWith(usPrefix)
    ? `支援市場：港股、中國 A 股與美股。美股示例：${note.slice(usPrefix.length)}`
    : undefined;
}

const lbCoverageByIdZh = new Map<string, string>(
  endpoints.flatMap((endpoint) => {
    const translated = coverageNoteZh(endpoint.notes?.[1]);
    return translated ? [[endpoint.id, translated] as [string, string]] : [];
  }),
);

const lbExtraNotesZh: Record<string, string[]> = {
  'lb-hk-quotes': ['美股報價仍由既有的美股接口提供。'],
  'lb-market-ah-premium': ['請傳入兩地上市配對中的港股一方，例如 2318.HK；傳入同一發行人的 A 股代碼會取得空序列。'],
  'lb-market-ah-premium-intraday': ['請傳入兩地上市配對中的港股一方，例如 2318.HK；傳入同一發行人的 A 股代碼會取得空序列。'],
  'lb-fundamental-industry-peer-hierarchy': ['同業層級由發行人各自披露，沒有披露的證券會取得空的層級鏈。'],
  'lb-content-topics': ['「我的話題」屬於帳戶範圍的資料，因此刻意不對外開放。'],
  'lb-content-security-news': ['若希望合併所有覆蓋該證券的上游新聞來源，請改用 /reference/news。'],
};

const lbFundamentalNoteZh = '財務報表與各項基本面接口同時支援港股、中國 A 股與美股代碼。';
const lbUsOnlyNoteZh = '傳入非美股代碼會回傳 400；港股與 A 股基本面請改用共用的 /fundamentals 接口。';

/** 一行一個端點：[id, 標題, 摘要]。 */
const longbridgeZhRows: ReadonlyArray<readonly [string, string, string]> = [
  ['lb-hk-quotes', '報價', '港股與中國 A 股的即時第一層報價。'],
  ['lb-security-static', '證券基礎資料', '名稱、交易所、貨幣、每手股數、股本、每股盈利與衍生品標記。'],
  ['lb-intraday', '分時線', '當日的分時價格、成交量、成交額與均價序列。'],
  ['lb-candlesticks', '歷史 K 線', '歷史 OHLCV K 線，包含已驗證可用的港股與中國 A 股分鐘級歷史。'],

  ['lb-analytics-capital-flow', '資金流向', '當日淨資金流向的時間序列。'],
  ['lb-analytics-capital-distribution', '資金分佈', '大單、中單與小單的流入與流出分佈。'],
  ['lb-analytics-calc-indexes', '計算指標', '漲跌、成交、估值、資金流、波動與衍生品相關的計算指標。'],
  ['lb-analytics-filings', '公司公告', '公司公告與披露文件。'],
  ['lb-analytics-short-positions', '淡倉', '港股與美股的已申報淡倉數據。'],
  ['lb-analytics-short-volume', '每日沽空成交', '港股與美股的每日沽空成交紀錄。'],

  ['lb-fundamental-financial-report', '財務報表', '損益表、資產負債表與現金流量表。'],
  ['lb-fundamental-analyst-ratings', '分析師評級', '目前的分析師評級數據。'],
  ['lb-fundamental-dividends', '派息', '派息歷史與各項關鍵日期。'],
  ['lb-fundamental-valuations', '估值', '目前的市盈率、市帳率、市銷率與股息率。'],
  ['lb-fundamental-company-profile', '公司概況', '公司身分、簡介與基本資料。'],
  ['lb-fundamental-executives', '管理層', '管理層與董事會成員資料。'],
  ['lb-fundamental-shareholders', '主要股東', '主要股東紀錄。'],
  ['lb-fundamental-fund-holdings', '基金持倉', '持有該證券的基金與 ETF。'],
  ['lb-fundamental-corporate-actions', '公司行動', '派息、拆股、回購與其他發行人事件。'],
  ['lb-fundamental-institution-rating', '機構評級', '最新機構評級，並附歷史一致預期摘要。'],
  ['lb-fundamental-institution-rating-detail', '機構評級明細', '歷史機構評級明細。'],
  ['lb-fundamental-dividend-detail', '派息明細', '詳細派息紀錄。'],
  ['lb-fundamental-forecast-eps', '預測每股盈利', '每股盈利的一致預測。'],
  ['lb-fundamental-financial-consensus', '財務一致預期', '詳細的財務一致預期數據。'],
  ['lb-fundamental-valuation-history', '估值歷史', '歷史估值觀測值。'],
  ['lb-fundamental-industry-valuation', '行業估值比較', '該證券與所屬行業的估值比較。'],
  ['lb-fundamental-industry-valuation-distribution', '行業估值分佈', '行業內的估值分佈。'],
  ['lb-fundamental-investment-relations', '投資關係', '發行人的對外投資關係資料。'],
  ['lb-fundamental-operating-metrics', '經營指標', '經營指標與業績摘要。'],
  ['lb-fundamental-buyback', '股份回購', '發行人的回購紀錄。'],
  ['lb-fundamental-macroeconomic-indicators', '宏觀經濟指標', '可搜尋的宏觀經濟指標目錄。'],
  ['lb-fundamental-business-segments', '業務分部', '最新一期依分部劃分的收入與經營拆解。'],
  ['lb-fundamental-macroeconomic-history', '宏觀經濟歷史數據', '單一宏觀經濟指標的歷史觀測值。'],
  ['lb-fundamental-business-segments-history', '業務分部歷史', '歷年業務分部拆解。'],
  ['lb-fundamental-institutional-rating-views', '機構評級觀點時間線', '機構評級觀點的時間序列。'],
  ['lb-fundamental-industry-ranking', '行業排行', '按當日漲跌排列市場內所有行業，並附各行業的領漲證券。'],
  ['lb-fundamental-industry-peer-hierarchy', '行業同業層級', '該證券的行業歸屬與同業關係。'],
  ['lb-fundamental-financial-report-snapshot', '財報摘要', '精簡的業績與財報摘要。'],
  ['lb-fundamental-top-shareholders', '十大股東', '依持股排名的股東紀錄。'],
  ['lb-fundamental-shareholder-detail', '股東持股明細', '單一股東物件的持股歷史。'],
  ['lb-fundamental-valuation-comparison', '估值對比', '與指定同業比較各項估值指標。'],
  ['lb-fundamental-etf-asset-allocation', 'ETF 資產配置', '美股、港股或 A 股上市基金的持倉，以及地區、資產類別與行業配置。非基金代碼會回傳空集合。'],

  ['lb-market-status', '市場狀態', '各支援市場目前的交易狀態。'],
  ['lb-market-temperature', '市場溫度', '目前的市場廣度與市場溫度指標。'],
  ['lb-market-temperature-history', '歷史市場溫度', '歷史市場溫度觀測值。'],
  ['lb-market-trading-sessions', '交易時段', '各市場每日的交易時段區間。'],
  ['lb-market-trading-days', '交易日', '指定日期範圍內的全日與半日交易日。'],
  ['lb-market-unusual-items', '異動項目', '價格與成交量的異動事件。'],
  ['lb-market-top-movers', '市場異動', '全市場出現異常波動的證券。'],
  ['lb-market-rank-categories', '排行榜分類', '人氣排行榜可用的分類鍵值。'],
  ['lb-market-popularity', '人氣排行榜', '指定分類下的證券排行。'],
  ['lb-market-broker-holdings', '經紀持倉', '港股證券的經紀持倉變動前列。'],
  ['lb-market-broker-holding-detail', '經紀持倉明細', '港股證券的完整經紀持倉明細。'],
  ['lb-market-broker-holding-history', '經紀持倉歷史', '單一經紀的每日持倉歷史。'],
  ['lb-market-ah-premium', 'A/H 溢價歷史', '兩地上市港股證券的歷史 A/H 溢價 K 線。'],
  ['lb-market-ah-premium-intraday', 'A/H 溢價分時', '兩地上市港股證券當日的 A/H 溢價觀測值。'],
  ['lb-market-trade-statistics', '成交統計', '主動買入、主動賣出與中性成交的統計。'],
  ['lb-market-index-constituents', '指數成分股', '指數的成分證券，例如 HSI.HK。'],

  ['lb-calendar-earnings', '業績公佈日曆', '指定日期範圍內的業績公佈事件。'],
  ['lb-calendar-dividends', '派息日曆', '指定日期範圍內的派息事件。'],
  ['lb-calendar-splits', '拆股日曆', '指定日期範圍內的拆股事件。'],
  ['lb-calendar-ipos', '新股日曆', '指定日期範圍內的新股上市事件。'],
  ['lb-calendar-macro', '宏觀數據日曆', '指定日期範圍內的宏觀經濟數據發佈。'],
  ['lb-calendar-closures', '休市日曆', '指定日期範圍內的交易所休市日。'],
  ['lb-calendar-meetings', '會議日曆', '指定日期範圍內的公司會議事件。'],
  ['lb-calendar-mergers', '合併日曆', '指定日期範圍內的合併與合股事件。'],

  ['lb-fundamental-us-company-overview', '美股公司概況', '美股上市公司的業務簡介、市值與排行標籤。'],
  ['lb-fundamental-us-valuation-overview', '美股估值概況', '估值百分位、行業排名與敘述式估值摘要。'],
  ['lb-fundamental-us-financial-overview', '美股財務概況', '多年度的收入、盈利、利潤率與資產負債摘要。'],
  ['lb-fundamental-us-financial-statement', '美股財務報表', '逐項列示的損益表、資產負債表或現金流量表。'],
  ['lb-fundamental-us-key-metrics', '美股關鍵財務指標', '盈利能力、成長、償債與營運效率的指標歷史。'],
  ['lb-fundamental-us-analyst-consensus', '美股分析師一致預期', '各報告期每股盈利、收入與息稅前利潤的實際值與預期值。'],
  ['lb-fundamental-us-company-dividends', '美股公司派息', '美股公司的派息與回購收益率歷史。'],
  ['lb-fundamental-us-etf-dividends', '美股 ETF 派息', '美股 ETF 的近十二個月與各財政年度派息歷史。'],
  ['lb-fundamental-us-etf-files', '美股 ETF 文件', '美股 ETF 的招股書、年報與半年報連結。'],

  ['lb-content-topics', '個股話題', '與該證券相關的唯讀內容話題。'],
  ['lb-content-security-news', '個股新聞', '單一上游來源的個股新聞，文章結構與 /reference/news 一致。'],

  ['lb-warrant-quotes', '認股證報價', '一檔或多檔港股認股證的即時報價。'],
  ['lb-warrant-issuers', '認股證發行商', '香港認股證發行商的參考清單。'],
  ['lb-warrant-screener', '認股證篩選器', '就指定港股正股篩選並排序認股證。'],
];

/**
 * 美股基本面：Eroya 先向合約上游取數，僅在該上游沒有資料時才改用公開上游，
 * 因此回應永遠是單一且最新可得的結果，而非多來源合併。
 * 一行一個端點：[id, 標題, 摘要]。
 */
const usFundamentalZhRows: ReadonlyArray<readonly [string, string, string]> = [
  ['us-income-statement', '美股利潤表', '按會計期間的利潤表明細，並可取得近十二個月重述數。'],
  ['us-balance-sheet', '美股資產負債表', '按會計期間的資產負債表明細。'],
  ['us-cash-flow', '美股現金流量表', '按會計期間的現金流量表明細，並可取得近十二個月重述數。'],
  ['us-earnings', '美股業績摘要', '按季度的實際與預期每股盈利，並附收入及盈利歷史。'],
  ['us-earnings-calendar', '美股財報日程', '下一次財報公布日與業績電話會議，並附一致預期的每股盈利與收入。'],
  ['us-earnings-dates', '美股財報日期', '過往及已排期的財報事件，包含預期每股盈利、實際每股盈利與超預期幅度。'],
  ['us-earnings-history', '美股歷史業績', '按季度的實際每股盈利對比預期，包含差額與超預期百分比。'],
  ['us-sec-filings', '美股 SEC 文件', '近期 SEC 申報文件，並附各份申報的附件文件。'],
  ['us-recommendations', '美股分析師評級', '當前分析師評級分布與一致評級。'],
  ['us-recommendation-summary', '美股評級走勢', '過去數月的評級分布，用於追蹤一致預期的變化。'],
  ['us-upgrades-downgrades', '美股評級調整', '按機構列出的評級調整紀錄，包含調整前後的目標價。'],
  ['us-analyst-price-targets', '美股目標價', '最低、平均、中位及最高目標價，並對比現價。'],
  ['us-earnings-estimate', '美股盈利預測', '按預測期間的每股盈利一致預期，包含分析師數目與預測區間。'],
  ['us-revenue-estimate', '美股收入預測', '按預測期間的收入一致預期，包含分析師數目與預測區間。'],
  ['us-eps-trend', '美股每股盈利趨勢', '各預測期間的每股盈利一致預期在過去 90 日的變化。'],
  ['us-eps-revisions', '美股盈利預測修訂', '按預測期間統計上調與下調的每股盈利預測次數。'],
  ['us-growth-estimates', '美股增長預測', '按預測期間的預期盈利增長率。'],
  ['us-sustainability', '美股 ESG 評分', '環境、社會及管治風險評分與爭議事件級別。'],
  ['us-insider-transactions', '美股內部人交易', '內部人買賣紀錄，包含申報人、關係、股數與金額。'],
  ['us-insider-purchases', '美股內部人買賣統計', '過去六個月的內部人買入與賣出總覽。'],
  ['us-insider-roster', '美股內部人名冊', '已登記內部人的職務、最近交易與持股。'],
  ['us-major-holders', '美股主要持股分佈', '內部人與機構各自持有的股本比例。'],
  ['us-institutional-holders', '美股機構持股', '機構持股人及其持倉規模、市值與期間變動。'],
  ['us-mutualfund-holders', '美股基金持股', '基金持股人及其持倉規模、市值與期間變動。'],
  ['us-fund-profile', '美股基金概況及持倉', 'ETF 或基金的基金公司、分類、費用、資產配置與主要持倉。'],
];

const usFundamentalNoteZh = 'Eroya 會跨多個上游解析此資料集，並回傳第一個涵蓋該證券的結果，因此回應為單一且最新可得的答案，而非多來源合併。';

const usFundamentalParamsZh: Record<string, string> = {
  ...lbParamsZh,
  ticker: '標準美股代碼，此類資料集僅支援美股。',
  period: '報告期間：annual(預設)、quarterly，或 ttm(近十二個月重述數)。',
  limit: '回傳筆數，範圍 1 至 200。',
};

const usFundamentalExtraNotesZh: Record<string, string[]> = {
  'us-balance-sheet': ['資產負債表為時點報表，因此不支援 period=ttm，傳入會回傳 400。'],
  'us-sustainability': ['上游目前已暫停提供 ESG 評分，因此在重新發布前，大部分證券會回傳空結果。'],
  'us-fund-profile': ['需傳入基金代碼；非基金證券會回傳空結果。'],
};

const usFundamentalZhOverlays: Record<string, EndpointOverlay> = Object.fromEntries(
  usFundamentalZhRows.map(([slug, title, summary]) => [`us-fundamental-${slug}`, {
    title,
    summary,
    description: `${summary}Eroya 會代為簽署私有上游請求，並以統一的回應外層格式回傳，用戶端只需使用自己的 Eroya API 金鑰。`,
    useCases: lbUseCasesZh,
    notes: [
      lbReadOnlyNoteZh,
      '支援市場：僅美股。傳入非美股代碼會回傳 400。',
      usFundamentalNoteZh,
      ...(usFundamentalExtraNotesZh[slug] ?? []),
    ],
    params: usFundamentalParamsZh,
  } satisfies EndpointOverlay]),
);

const longbridgeZhOverlays: Record<string, EndpointOverlay> = Object.fromEntries(
  longbridgeZhRows.map(([id, title, summary]) => {
    const fundamental = id.startsWith('lb-fundamental-');
    const usOnly = id.startsWith('lb-fundamental-us-');
    return [id, {
      title,
      summary,
      description: `${summary}Eroya 會代為簽署私有上游請求，並以統一的供應商外層格式回傳，用戶端只需使用自己的 Eroya API 金鑰。`,
      useCases: lbUseCasesZh,
      notes: [
        lbReadOnlyNoteZh,
        ...(lbCoverageByIdZh.has(id) ? [lbCoverageByIdZh.get(id)!] : []),
        ...(usOnly ? [lbUsOnlyNoteZh] : fundamental ? [lbFundamentalNoteZh] : []),
        ...(lbExtraNotesZh[id] ?? []),
      ],
      params: lbParamsZh,
    } satisfies EndpointOverlay];
  }),
);

export const endpointsZhHK: Record<string, EndpointOverlay> = {
  ...longbridgeZhOverlays,
  ...usFundamentalZhOverlays,
  'us-stocks-custom-bars': {
    title: '自訂 K 線 (OHLC)',
    summary: '自訂區間的歷史 OHLC 與成交量。',
    description:
      '取得美股在盤前、盤中與盤後各時段可依除權調整的聚合 K 線。若某區間沒有資料，代表該區間內沒有符合條件的成交。',
    useCases: ['繪製圖表', '技術分析', '回測', '市場研究'],
    params: aggregateParamsZh,
  },
  'us-options-custom-bars': {
    title: '自訂 K 線 (OHLC)',
    summary: '單一期權合約的歷史聚合 K 線。',
    description:
      '取得指定期權合約在自訂區間內的 OHLC、成交量、成交筆數與 VWAP。請使用標準的 O: 合約代碼。',
    useCases: ['期權繪圖', '波動率研究', '回測', '合約分析'],
    params: aggregateParamsZh,
  },
  'us-options-trades': {
    title: '逐筆成交',
    summary: '期權合約的逐筆(tick)成交紀錄。',
    description:
      '取得指定期權合約在某時間範圍內完整的逐筆成交資料。每筆紀錄包含價格、數量、交易所、成交條件與精確時間戳，可用於盤中分析、市場微結構研究、回測，以及可稽核的市場活動歷史。',
    useCases: ['盤中分析', '演算法交易', '市場微結構研究', '資料完整性與合規'],
    notes: [
      '由設定的 Options Advanced 上游即時提供。',
      '完整歷史紀錄可回溯至 2014 年 6 月 2 日。',
      '時間戳為奈秒精度的 Unix 時間戳。',
    ],
    params: {
      optionsTicker: '以 O: 為前綴的標準期權合約代碼。',
      timestamp: '依成交時間戳查詢：可用 YYYY-MM-DD 日期或奈秒時間戳，支援 .gt、.gte、.lt、.lte 修飾符。',
      order: '依排序欄位排序：asc 或 desc。',
      limit: '每頁結果數，預設 1,000，最大 50,000。',
      sort: '排序欄位，預設為 timestamp。',
    },
  },
  'us-options-quotes': {
    title: '報價',
    summary: '期權合約的歷史買賣報價。',
    description:
      '取得指定期權合約在某時間範圍內的歷史報價。每筆紀錄提供買價、賣價、掛單量、交易所代號與精確時間戳，反映各時點的期權市場狀況。',
    useCases: ['歷史報價分析', '市場關注度評估', '演算法回測', '策略優化'],
    notes: [
      '由設定的 Options Advanced 上游即時提供。',
      '完整歷史紀錄可回溯至 2022 年 3 月 7 日。',
      '報價數量以整手表示，一般每手為 100 股。',
    ],
    params: {
      optionsTicker: '以 O: 為前綴的標準期權合約代碼。',
      timestamp: '依報價時間戳查詢：可用 YYYY-MM-DD 日期或奈秒時間戳，支援 .gt、.gte、.lt、.lte 修飾符。',
      order: '依排序欄位排序：asc 或 desc。',
      limit: '每頁結果數，預設 1,000，最大 50,000。',
      sort: '排序欄位，預設為 timestamp。',
    },
  },
  'us-trades': {
    title: '逐筆成交',
    summary: '美股逐筆(tick)成交紀錄。',
    description:
      '取得指定美股在某時間範圍內完整的逐筆成交資料。每筆紀錄包含價格、數量、交易所、成交條件與奈秒級時間戳。這類細粒度資料是構建聚合 K 線與市場微結構分析的基礎，涵蓋每一筆計入 OHLC 的合格成交。',
    useCases: ['盤中分析', '演算法交易', '市場微結構研究', '資料完整性與合規'],
    notes: ['最高方案的紀錄可回溯至 2003 年 9 月 10 日。', '時間戳為奈秒精度的 Unix 時間戳。'],
    params: {
      stockTicker: '區分大小寫的股票代碼。',
      timestamp: '依成交時間戳查詢：可用 YYYY-MM-DD 日期或奈秒時間戳，支援 .gt、.gte、.lt、.lte 修飾符。',
      order: '依排序欄位排序：asc 或 desc。',
      limit: '每頁結果數，預設 1,000，最大 50,000。',
      sort: '排序欄位，預設為 timestamp。',
    },
  },
  'us-sma': {
    title: '簡單移動平均 (SMA)',
    summary: '指定窗口的簡單移動平均線。',
    description:
      '取得指定代碼在某時間範圍內的簡單移動平均(SMA)。SMA 對一定期數的價格取平均，平滑波動以呈現潛在趨勢與訊號。',
    useCases: ['趨勢分析', '交易訊號(SMA 交叉)', '判斷支撐/壓力', '進出場時點'],
    notes: ['以日聚合搭配窗口 10，即得到 10 日移動平均。'],
    params: {
      stockTicker: '區分大小寫的股票代碼。',
      timestamp: '依時間戳查詢：可用 YYYY-MM-DD 日期或毫秒時間戳，支援 .gt、.gte、.lt、.lte 修飾符。',
      timespan: '聚合時間窗口：minute、hour、day、week、month、quarter 或 year。',
      adjusted: '所用聚合是否依股票分割調整，預設為 true。',
      window: '計算 SMA 的窗口大小，預設為 50。',
      series_type: '每個聚合採用的價格：open、high、low 或 close，預設為 close。',
      expand_underlying: '是否在回應中一併包含計算所用的聚合資料。',
      order: '依時間戳排序：asc 或 desc，預設為 desc。',
      limit: '每頁結果數，預設 10，最大 5,000。',
    },
  },
  'us-news': {
    title: '新聞',
    summary: '合併所有覆蓋該證券之上游來源的統一格式公司新聞。',
    description:
      '以單一 Eroya 回應格式取得最新公司新聞。新聞是唯一採用合併而非擇一的資料集：所有覆蓋該證券的上游來源會並行查詢，並依時間排序合併為單一清單，同時以標準化網址與標題去重，因此同一則通訊社稿件只會出現一次。若其中一個上游部分失敗，錯誤會列於 errors，已成功取得的文章仍會照常回傳。',
    useCases: ['市場情緒分析', '投資研究', '自動化監控', '投組策略優化'],
    params: {
      ticker: '標準格式代碼：AAPL.US、700.HK、000001.SZ 或 600519.SH。',
      limit: '排序與去重後的合併結果筆數，預設 50，最大 100。',
    },
    notes: [
      '美股請求可用的上游來源多於港股與 A 股，因此美股的合併清單覆蓋面最廣。回應不會標示文章來自哪一個上游；文章網址指向原始刊登媒體，並且原樣回傳。',
    ],
  },
  'us-options-contracts': {
    title: '全部合約',
    summary: '有效與到期期權合約的索引。',
    description:
      '取得涵蓋有效與到期的期權合約完整索引，可全面查詢或限定於特定標的股票。每筆包含合約類型(買權/賣權)、行權方式、到期日與行權價，可用於市場可用性分析與期權策略研究。',
    useCases: ['市場可用性分析', '策略開發', '研究與建模', '合約探索'],
    notes: ['已棄用的 ticker 參數不再支援；查詢單一合約請改用期權合約接口。'],
    params: {
      underlying_ticker: '查詢與某標的股票相關的合約，支援篩選修飾符。',
      contract_type: '依合約類型篩選：call、put 或 other。',
      expiration_date: '依合約到期日篩選(YYYY-MM-DD)，支援篩選修飾符。',
      as_of: '合約在此日期(YYYY-MM-DD)的時間點狀態，預設為今日。',
      strike_price: '依行權價篩選，支援篩選修飾符。',
      expired: '查詢已到期的合約，預設為 false。',
      order: '依排序欄位排序：asc 或 desc。',
      limit: '每頁結果數，預設 10，最大 1,000。',
      sort: '排序欄位，預設為 ticker。',
    },
  },
  'us-balance-sheets': {
    title: '資產負債表',
    summary: '季度與年度的財務狀況快照。',
    description:
      '取得上市公司申報的標準化資產、負債與權益。每筆為某會計期間的時間點快照，而非累計流量。',
    useCases: ['基本面分析', '估值', '負債分析', '股票研究'],
    params: {
      cik: 'SEC 中央索引碼(CIK)。',
      tickers: '篩選代碼陣列包含此代碼的紀錄。',
      period_end: '期末日期(YYYY-MM-DD)，支援 .gt、.gte、.lt、.lte 後綴。',
      filing_date: '包含該期間的最近一次 SEC 申報日期，支援比較後綴。',
      fiscal_year: '會計年度，支援 .gt、.gte、.lt、.lte 後綴。',
      fiscal_quarter: '會計季度，1 至 4。',
      timeframe: '報表週期：quarterly 或 annual。',
      limit: '每頁結果數，預設 100，最大 50,000。',
      sort: '以逗號分隔的欄位加 .asc 或 .desc。',
    },
  },
  'us-financial-ratios': {
    title: '財務比率',
    summary: '估值、獲利、流動性與槓桿比率。',
    description:
      '取得依最近交易日、以近十二個月(TTM)財報與每日市價計算的公司比率。',
    useCases: ['公司估值', '同業比較', '財務健康', '選股篩選'],
    notes: ['數值型查詢欄位支援 .gt、.gte、.lt、.lte 等比較修飾符。'],
    params: {
      ticker: '股票代碼，支援篩選修飾符。',
      cik: 'SEC 中央索引碼(CIK)，支援篩選修飾符。',
      price: '計算所用的股價。',
      average_volume: '近 30 個交易日的平均成交量。',
      market_cap: '市值。',
      earnings_per_share: '歸屬普通股股東的加權每股淨利。',
      price_to_earnings: '股價除以正值每股盈餘。',
      price_to_book: '股價除以每股淨值。',
      price_to_sales: '股價除以每股營收。',
      price_to_cash_flow: '股價除以正值每股營運現金流。',
      price_to_free_cash_flow: '股價除以正值每股自由現金流。',
      dividend_yield: '每股年度股利除以股價。',
      return_on_assets: '淨利除以總資產。',
      return_on_equity: '淨利除以股東權益。',
      debt_to_equity: '短期與長期負債除以股東權益。',
      current: '流動資產除以流動負債。',
      quick: '流動資產扣除存貨後除以流動負債。',
      cash: '現金及約當現金除以流動負債。',
      ev_to_sales: '企業價值除以營收。',
      ev_to_ebitda: '企業價值除以 EBITDA。',
      enterprise_value: '市值加負債減現金及約當現金。',
      free_cash_flow: '營運現金流減資本支出。',
      limit: '每頁結果數，預設 100，最大 50,000。',
      sort: '以逗號分隔的欄位加 .asc 或 .desc，預設為 ticker.asc。',
    },
  },
  'cn-daily': {
    title: '每日行情',
    summary: '未復權的 A 股每日 OHLC 與成交狀況。',
    description:
      '收盤後發布的 A 股每日行情。停牌標的無資料列。價格為未復權，需要時請搭配復權因子資料集。',
    useCases: ['每日篩選', '歷史分析', '市場廣度', '回測'],
    notes: ['如需全市場擷取，請以交易日期(而非證券代碼)逐日迭代。', '日期使用 YYYYMMDD。'],
    params: commonParams,
  },
  'cn-cyq-perf': {
    title: '籌碼成本與獲利比例',
    summary: '每日持倉成本分布與獲利比例。',
    description:
      '取得每日持倉成本分位數、歷史區間、加權平均成本與獲利持倉比例。資料自 2018 年起。',
    useCases: ['成本分析', '籌碼訊號', '支撐/壓力', '每日篩選'],
    notes: ['每次呼叫最多 6,000 列，請分頁或縮小查詢日期範圍。'],
    params: { ...commonParams, ts_code: 'TS 證券代碼(必填)。' },
  },
  'cn-technical-factors': {
    title: '技術因子',
    summary: '每日復權價格與量化技術指標。',
    description:
      '取得為 A 股產生的全歷史每日價格因子與技術指標。指標以前復權價格計算。',
    useCases: ['因子研究', '訊號生成', '趨勢分析', '量化選股'],
    notes: [
      '每次呼叫最多 10,000 列，請分頁或縮小查詢範圍。',
      '前復權歷史為錨定最新交易日的每日快照，不會回溯更新。',
      '動態 pro_bar 復權因錨定於請求的 end_date，結果可能不同。',
      '技術指標以前復權價格計算。',
    ],
    params: commonParams,
  },
  'cn-balance-sheet': {
    title: '資產負債表',
    summary: '單一上市 A 股公司的申報資產負債表。',
    description:
      '取得上市公司歷史資產負債表科目，涵蓋一般工商、銀行、保險與證券公司的報表格式。',
    useCases: ['基本面分析', '償債研究', '季度篩選', '公司估值'],
    notes: ['每次請求限一家公司。全市場季度擷取請改用以日期為主的進階資料集。'],
    params: {
      ts_code: 'TS 證券代碼。此資料集每次請求限一家公司。',
      ann_date: '公告日期，格式為 YYYYMMDD。',
      start_date: '公告日期範圍起點。',
      end_date: '公告日期範圍終點。',
      period: '報告期末，例如 20251231 代表年報。',
      report_type: '報表類型。',
      comp_type: '公司類型：1 工商、2 銀行、3 保險、4 證券。',
      fields: '以逗號分隔的輸出欄位。',
    },
  },
  'cn-disclosure-date': {
    title: '財報披露計劃',
    summary: '定期財報的預計與實際披露日期。',
    description:
      '取得定期財報的披露計劃，包含預計披露日、最新公告日與各報告期的實際披露日。適合預判財報發布與建立事件行事曆。',
    useCases: ['財報行事曆', '事件驅動策略', '追蹤覆蓋', '財報季規劃'],
    notes: ['每次呼叫最多 6,000 列。', '日期使用 YYYYMMDD。'],
    params: {
      ts_code: 'TS 證券代碼。',
      end_date: '報告期末，格式為 YYYYMMDD(例如 20181231 為 2018 年報，20180630 為中報)。',
      pre_date: '預計披露日期，格式為 YYYYMMDD。',
      ann_date: '最新披露公告日期，格式為 YYYYMMDD。',
      actual_date: '實際披露日期，格式為 YYYYMMDD。',
    },
  },
  'indices-custom-bars': {
    title: '自訂 K 線 (OHLC)',
    summary: '按自訂區間聚合的歷史指數點位。',
    description:
      '取得指數在自訂日期區間與時間粒度(美東時間)下聚合的開、高、低、收點位。與股票及期權不同，指數聚合來自各指數發布的點位而非個別成交，因此若某區間內沒有新的指數點位，就不會產生該區間的 K 線。',
    useCases: ['基準比較', '市場趨勢分析', '資料視覺化', '研究與建模'],
    params: {
      indicesTicker: '帶有標準 I: 前綴的指數代碼。',
      multiplier: '時間跨度的倍數。',
      timespan: '聚合窗口：minute、hour、day、week、month、quarter 或 year。',
      from: '窗口起點，格式為 YYYY-MM-DD 或 Unix 毫秒時間戳。',
      to: '窗口終點，格式為 YYYY-MM-DD 或 Unix 毫秒時間戳。',
      sort: '依時間戳排序：asc 或 desc。',
      limit: '查詢的基礎聚合上限，預設 5,000，最大 50,000。',
    },
    notes: [
      '指數代碼使用標準 I: 前綴，請求會自動路由至指數權限。',
      '指數 K 線並非由成交構建，因此不含成交量、VWAP 或成交筆數。',
    ],
  },
  'indices-previous-day-bar': {
    title: '前一交易日 K 線',
    summary: '單一指數前一交易日的 OHLC。',
    description:
      '取得指數前一個交易日的開、高、低、收點位。無需自行推算日期區間，即可建立前一交易日的比較基準。',
    useCases: ['基準比較', '每日報表', '技術分析', '市場研究'],
    params: {
      indicesTicker: '帶有標準 I: 前綴的指數代碼。',
    },
    notes: ['「前一交易日」指最近一個已完成的交易日，盤中不會變動。'],
  },
  'indices-daily-summary': {
    title: '單日行情摘要',
    summary: '單一指數在指定日期的開盤與收盤點位。',
    description:
      '取得指數在指定日期的開盤與收盤點位，以及當日高低點與盤前、盤後時段所計算的點位。部分指數的編製方法會在常規時段以外發布點位，未發布者則不會回傳相應欄位。',
    useCases: ['每日表現分析', '歷史資料收集', '盤後洞察', '投資組合追蹤'],
    params: {
      indicesTicker: '帶有標準 I: 前綴的指數代碼。',
      date: '交易日期，格式為 YYYY-MM-DD。',
    },
    notes: ['指數常規時段為美東時間星期一至星期五 09:30 至 16:00。'],
  },
  'indices-snapshot': {
    title: '指數快照',
    summary: '一次取得多個指數的即時點位與當日表現。',
    description:
      '取得一組指數的快照資料，包含目前點位、當日開高低、前收盤價，以及絕對與百分比漲跌幅。單次請求即可涵蓋整份自選清單，適合需要持續更新而不想逐一輪詢的儀表板。',
    useCases: ['市況評估', '市場情緒追蹤', '儀表板看板', '投資組合脈絡'],
    params: {
      'ticker.any_of': '以逗號分隔的指數代碼清單。',
      ticker: '單一指數代碼，支援 .gt、.gte、.lt、.lte 範圍修飾詞。',
      order: '結果排序方向：asc 或 desc。',
      limit: '每頁結果數，預設 10，最大 250。',
      sort: '排序欄位，預設為 ticker。',
    },
    notes: ['使用 ticker.any_of 可於單次請求中取得整份自選清單。'],
  },
  'indices-tickers': {
    title: '全部指數代碼',
    summary: '可查詢的指數代碼目錄。',
    description:
      '取得 Eroya 支援的指數代碼，包含指數名稱、來源機構與是否仍在發布。以 market=indices 篩選共用的代碼目錄即可只取得指數，涵蓋標普、Nasdaq、道瓊斯、FTSE Russell、MSCI、晨星、Cboe Global Indices 與法國興業銀行等超過 10,000 檔指數。',
    useCases: ['指數探索', '代碼驗證', '自選清單建立', '資料整合'],
    params: {
      market: '設為 indices 以列出指數代碼。',
      ticker: '指定單一指數代碼，支援 .gt、.gte、.lt、.lte 修飾詞。',
      search: '對指數名稱進行不分大小寫的子字串比對。',
      active: '是否仍在發布，預設為 true。',
      order: '結果排序方向：asc 或 desc。',
      limit: '每頁結果數，預設 100，最大 1,000。',
      sort: '排序欄位，預設為 ticker。',
    },
    notes: ['market=indices 是將請求路由至指數權限的關鍵；未指定時目錄會由股票代碼回應。'],
  },
  'us-income-statements': {
    title: '利潤表',
    summary: '季度與年度的收入、費用與盈餘流量。',
    description:
      '取得上市公司經標準化的利潤表。與資產負債表不同，這些數據是某一會計期間內的流量，因此同一年度的季度與年度紀錄描述的是不同區間，不可混用於同一序列。',
    useCases: ['基本面分析', '盈餘趨勢研究', '利潤率分析', '選股與建模'],
    params: {
      cik: 'SEC 中央索引碼(CIK)。',
      tickers: '篩選代碼陣列包含此代碼的紀錄。',
      period_end: '期末日期(YYYY-MM-DD)，支援 .gt、.gte、.lt、.lte 後綴。',
      filing_date: '包含該期間的最近一次 SEC 申報日期，支援比較後綴。',
      fiscal_year: '會計年度，支援 .gt、.gte、.lt、.lte 後綴。',
      fiscal_quarter: '會計季度，1 至 4。',
      timeframe: '報表週期：quarterly 或 annual。',
      limit: '每頁結果數，預設 100，最大 50,000。',
      sort: '以逗號分隔的欄位加 .asc 或 .desc。',
    },
    notes: ['季度與年度紀錄涵蓋不同區間，建立時間序列前請先以 timeframe 篩選。'],
  },
  'us-cash-flow-statements': {
    title: '現金流量表',
    summary: '各期間的營運、投資與融資現金流。',
    description:
      '取得上市公司經標準化的現金流量表。現金流是最不易受會計政策影響的報表，因此常用來檢驗申報盈餘的品質。',
    useCases: ['盈餘品質檢驗', '自由現金流建模', '資本配置研究', '估值'],
    params: {
      cik: 'SEC 中央索引碼(CIK)。',
      tickers: '篩選代碼陣列包含此代碼的紀錄。',
      period_end: '期末日期(YYYY-MM-DD)，支援 .gt、.gte、.lt、.lte 後綴。',
      filing_date: '包含該期間的最近一次 SEC 申報日期，支援比較後綴。',
      fiscal_year: '會計年度，支援 .gt、.gte、.lt、.lte 後綴。',
      fiscal_quarter: '會計季度，1 至 4。',
      timeframe: '報表週期：quarterly 或 annual。',
      limit: '每頁結果數，預設 100，最大 50,000。',
      sort: '以逗號分隔的欄位加 .asc 或 .desc。',
    },
  },
  'us-short-interest': {
    title: '空頭部位',
    summary: '每月兩次申報的空頭部位與回補天數。',
    description:
      '依結算日取得各證券申報的空頭部位，以及用於計算回補天數的平均日成交量。交易所每月收集兩次此類部位，因此該序列是結算日的快照，而非每日指標。',
    useCases: ['擁擠度與軋空分析', '情緒研究', '風險篩選', '事件研究'],
    params: {
      ticker: '股票代碼，支援篩選修飾詞。',
      settlement_date: '結算日期(YYYY-MM-DD)，支援 .gt、.gte、.lt、.lte 後綴。',
      days_to_cover: '依回補天數篩選，支援比較後綴。',
      limit: '每頁結果數，預設 100，最大 50,000。',
      sort: '以逗號分隔的欄位加 .asc 或 .desc，預設為 ticker.asc。',
    },
    notes: ['部位每月申報兩次，並於結算日後數個交易日才發布。'],
  },
  'us-short-volume': {
    title: '放空成交量',
    summary: '每日放空成交量及其佔總成交量的比重。',
    description:
      '取得各證券每日的放空成交量，以及作為比較基準的總成交量，可直接計算每日放空比率。這是交易活動指標，與每月申報兩次的結算日空頭部位並不相同。',
    useCases: ['每日情緒追蹤', '資金流分析', '訊號構建', '流動性研究'],
    params: {
      ticker: '股票代碼，支援篩選修飾詞。',
      date: '交易日期(YYYY-MM-DD)，支援 .gt、.gte、.lt、.lte 後綴。',
      short_volume_ratio: '依放空成交量比率篩選，支援比較後綴。',
      limit: '每頁結果數，預設 100，最大 50,000。',
      sort: '以逗號分隔的欄位加 .asc 或 .desc，預設為 ticker.asc。',
    },
    notes: ['放空成交量比率同時反映盤中造市行為與方向性賣壓，本身並非看空訊號。'],
  },
  'flatfiles-datasets': {
    title: '列出批量檔案資料集',
    summary: '所有批量檔案資料集及您方案的取用權限。',
    description:
      '回傳批量檔案目錄：可用於其他批量檔案接口的資料集代號、封存路徑、資料粒度、解鎖所需的最低方案，以及您目前的金鑰是否具備取用權限。下載本身不限流量 — 方案只決定可取用哪些資料集，不會按流量計費。',
    useCases: ['資料集探索', '權限檢查', '流程設定', '方案比較'],
    notes: [
      '日線及分鐘資料集自 Pro 方案起開放，逐筆成交與報價自 Premium 方案起開放。',
      '凡需填寫 dataset 參數之處，資料集代號與完整封存路徑皆可使用。',
    ],
  },
  'flatfiles-list': {
    title: '列出批量檔案',
    summary: '指定日期區間內已發布的檔案與其大小。',
    description:
      '列出某資料集在指定日期區間內已發布的批量檔案，並附上每個物件的實際位元組大小。清單僅包含實際有交易的交易日，因此是下載前列舉日期、以及在開始前估算傳輸量的可靠方式。',
    useCases: ['歷史回補規劃', '傳輸量估算', '增量同步', '缺漏偵測'],
    params: {
      dataset: '資料集代號或封存路徑。',
      start: '區間起始日(含)，格式為 YYYY-MM-DD。',
      end: '區間結束日(含)，格式為 YYYY-MM-DD，省略時等同 start。',
    },
    notes: ['單次查詢的區間不得超過 366 天。'],
  },
  'flatfiles-url': {
    title: '取得預簽下載連結',
    summary: '單一交易日檔案的限時直連網址。',
    description:
      '為單一交易日檔案回傳預簽的 HTTPS 網址。該連結為標準 S3 簽章網址，任何 HTTP 用戶端、S3 用戶端或資料框架程式庫皆可直接讀取，並支援分段(Range)讀取。Eroya 只負責簽發連結而不轉送檔案內容，下載速度僅取決於您自身的網路頻寬。',
    useCases: ['直接下載', '以 Polars 或 DuckDB 分段讀取', '並行歷史回補', '資料倉儲匯入'],
    params: {
      dataset: '資料集代號或封存路徑。',
      date: '交易日期，格式為 YYYY-MM-DD。',
      expiresIn: '連結有效秒數，範圍 60 至 3600，預設 900。',
    },
    notes: [
      '每個連結在有效期內僅授權單一檔案。請於需要時重新索取，不建議長期保存。',
      '各交易日檔案約於次一交易日美東時間上午 11:00 發布，過早索取會回傳 404。',
    ],
  },
  'flatfiles-download': {
    title: '下載批量檔案',
    summary: '直接轉址至已簽章的檔案。',
    description:
      '與預簽連結接口相同，但以 HTTP 302 直接轉址至簽章網址，而非回傳 JSON。任何會跟隨轉址的用戶端因此可將 Eroya 網址直接視為檔案本身，無需自行處理簽章流程。',
    useCases: ['curl 與 wget', 'Shell 管線', '以 pandas 直接讀取', 'Airflow 與 cron 排程'],
    params: {
      dataset: '資料集代號或封存路徑。',
      date: '交易日期，格式為 YYYY-MM-DD。',
      expiresIn: '連結有效秒數，範圍 60 至 3600，預設 900。',
    },
    notes: [
      '請為 curl 加上 -L，或在 requests 中啟用 allow_redirects，以便用戶端跟隨轉址。',
      '轉址目標為單一已簽章物件，請立即跟隨，不建議另行保存。',
    ],
  },
};
